import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Define the initial state of the form
const initialState = {
    formData: {
        title: '',
        description: '',
        thumbnail: '',
        startDateTime: '',
        endDateTime: '',
        allowChat: false,
        products: [],
        broadcastStartedAt: ''
    },
    status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
    error: null,
    validationErrors: {}, // Add a new field for validation errors
};

// Async thunk for fetching the live shopping form data
export const fetchLiveShoppingFormData = createAsyncThunk(
    'form/fetchLiveShoppingFormData',
    async ({liveShoppingId, customFetch}, { rejectWithValue }) => {
        try {
            // Replace with your API endpoint
            const response = await customFetch(`/api/get-live-shopping/${liveShoppingId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch room data');
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for submitting the form data
export const submitLiveShoppingForm = createAsyncThunk(
    'form/submitLiveShoppingForm',
    async ({filledFormData, customFetch}, { rejectWithValue, getState }) => {
        
        const { validationErrors, formData } = getState().liveShoppingForm;
        console.log(formData, validationErrors, Object.keys(validationErrors))
        // console.log()
        if (Object.keys(validationErrors).length > 0) {
            return rejectWithValue(Object.values(validationErrors)[0]);
        }
        try {
            // Replace with your API call logic
            const fd = new FormData()
            Object.keys(filledFormData).forEach(key => {
                if(key === 'products') {
                    let modifiedProduct = filledFormData[key]
                    modifiedProduct = modifiedProduct.map(({productId, variants}) => {
                        return {
                            productId,
                            variants: variants.map(variant => variant.id)
                        }
                    })
                    console.log(modifiedProduct)
                    fd.append(key, JSON.stringify(modifiedProduct))
                } else{
                    fd.append(key, typeof filledFormData[key] === 'string' ? filledFormData[key].trim() : filledFormData[key]);
                }
            })
            const response = await customFetch('/api/live-stream', {
                method: 'POST',
                body: fd,
                headers: {
                    // 'Content-Type': 'application/json'
                    dataType: 'json'
                }
            });
            let res = await response.json()
            if(res.status == 0) {
                throw new Error(res.data)
            }
            if (!response.ok) {
                throw new Error('Failed to create room');
            }
            return res;
        } catch (error) {
            console.error(error)
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for updating the form data
export const updateLiveShoppingForm = createAsyncThunk(
    'form/updateLiveShoppingForm',
    async ({updatedFormData, liveShoppingId, customFetch}, { rejectWithValue, getState }) => {
        const { validationErrors, formData } = getState().liveShoppingForm;
        if (Object.keys(validationErrors).length > 0) {
            return rejectWithValue(Object.values(validationErrors)[0]);
        }
        try {
            const fd = new FormData()
            Object.keys(updatedFormData).forEach(key => {
                if(key === 'products') {
                    let modifiedProduct = updatedFormData[key]
                    modifiedProduct = modifiedProduct.map(({productId, variants}) => {
                        return {
                            productId,
                            variants: variants.map(variant => variant.id)
                        }
                    })
                    console.log(modifiedProduct)
                    fd.append(key, JSON.stringify(modifiedProduct))
                } else if(Array.isArray(updatedFormData[key])) {
                    fd.append(key, JSON.stringify(updatedFormData[key]))
                } else{
                    fd.append(key, typeof updatedFormData[key] === 'string' ? updatedFormData[key].trim() : updatedFormData[key]);
                }
            })
            const response = await customFetch(`/api/live-stream/${liveShoppingId}`, {
                method: 'PUT',
                body: fd,
                headers: {
                    // 'Content-Type': 'application/json'
                    dataType: 'json'
                }
            });
            if (!response.ok) {
                throw new Error('Failed to update room');
            }
            return await response.json();
        } catch (error) {
            console.error(error)
            return rejectWithValue(error.message);
        }
    }
);

// The slice for the form
const liveShoppingFormSlice = createSlice({
    name: 'form',
    initialState,
    reducers: {
        // Reducer to update form data and validate fields
        updateAndValidateFormData: (state, action) => {
            let { name, value } = action.payload;
            state.formData[name] = value;
            console.log(`updateAndValidateFormData`, name, value)
            // Validate the field
            if(typeof value === 'string') value = value.trim();
            if (!value && name != 'allowChat'/*['title', 'description'].includes(name)*/) {
                console.log(`updateAndValidateFormData uu`, name, value)
                state.validationErrors[name] = `${name} is required`;
                return;
            } else {
                delete state.validationErrors[name]
            }
            if(name == 'startDateTime' && new Date(value) <= new Date()) {
                state.validationErrors[name] = `Start Date & Time Should be Greater Than Current Date & Time!`;
            }
        },
        resetForm: (state, action) => {
            Object.keys(initialState).forEach((key) => {
                state[key] = initialState[key]
            })
        },
        addValidationError: (state, action) => {
            state.validationErrors = {
                title: 'title is required',
                description: 'description is required',
                thumbnail: 'thumbnail is required',
                startDateTime: 'Start date time is required'
            }
        }
        // You can add more validation logic here
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitLiveShoppingForm.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(submitLiveShoppingForm.fulfilled, (state, action) => {
                state.status = `success,${action.payload?.data}`;
                state.validationErrors = {}; // Clear validation errors
            })
            .addCase(submitLiveShoppingForm.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            })
            .addCase(updateLiveShoppingForm.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateLiveShoppingForm.fulfilled, (state, action) => {
                state.status = `success,${action.payload?.data}`;
                state.validationErrors = {}; // Clear validation errors
            })
            .addCase(updateLiveShoppingForm.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            })
            .addCase(fetchLiveShoppingFormData.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchLiveShoppingFormData.fulfilled, (state, action) => {
                state.status = 'success';
                // Update the form data with the fetched data
                let data = action?.payload?.data
                state.formData = data;
            })
            .addCase(fetchLiveShoppingFormData.rejected, (state, action) => {
                state.status = `failed,${action.payload}`;
                state.error = action.payload;
            });
    }
});

export const { updateAndValidateFormData, resetForm, addValidationError } = liveShoppingFormSlice.actions;
export default liveShoppingFormSlice.reducer
